import type { Sport } from 'hooks'
import type { MarketType } from 'overtime-utils'
import { formatCamelCaseToSpaces, formatTextToSlug } from '@/utils/helper'

export interface PlayerProps {
  playerId: number
  playerName: string
}

export interface Market {
  typeId: MarketType
  line: number
  status: number
  odds: number[]
  gameId: string
  sport: string
  leagueId: number
  leagueName: string
  subLeagueId: number
  type: string
  originalMarketName: string
  maturity: number
  maturityDate: string
  homeTeam: string
  awayTeam: string
  isOpen: boolean
  isResolved: boolean
  isCancelled: boolean
  isPaused: boolean
  isOneSideMarket: boolean
  isPlayerPropsMarket: boolean
  isOneSidePlayerPropsMarket: boolean
  isYesNoPlayerPropsMarket: boolean
  playerProps: PlayerProps
  combinedPositions: any[][]
  positionNames?: string[]
  proof: any[]
  childMarkets: Market[]
  statusCode?: string
  homeScore?: number
  awayScore?: number
  gameClock?: string | null
  gamePeriod?: string
  homeScoreByPeriod?: number[]
  awayScoreByPeriod?: number[]
  live?: boolean
}

export interface Game {
  gameId: string
  subLeagueId: number
  typeId: MarketType
  line: number
  maturity: number
  homeTeam: string
  awayTeam: string
  status: number
  odds: number[]
  positionNames: string[]
  childMarkets: Market[]
  statusCode: string
  tournamentName: string
  sport: string
  initialSport: string
  leagueId: number
  leagueName: string
  type: string
  originalMarketName: string
  maturityDate: string
  isOpen: boolean
  isResolved: boolean
  isCancelled: boolean
  isPaused: boolean
  isOneSideMarket: boolean
  isPlayerPropsMarket: boolean
  isOneSidePlayerPropsMarket: boolean
  isYesNoPlayerPropsMarket: boolean
  playerProps: PlayerProps
  combinedPositions: any[][]
  homeScoreByPeriod: number[]
  awayScoreByPeriod: number[]
  live: boolean
  proof: any[]
  homeScore: number
  awayScore: number
  gameClock: string | null
  gamePeriod: string
}

export interface TransformedGame {
  id: string
  gameId: string
  slug: string
  title: string
  startsAt: string
  status: number
  statusCode: string
  turnover: string
  awayTeam: string
  homeTeam: string
  leagueId: number
  leagueName: string
  line: number
  odds: number[]
  positionNames: string[]
  typeId: MarketType
  sport: {
    sportId: string
    name: string
    slug: string
  }
  league: {
    id: number
    name: string
    slug: string
    tournamentName: string
  }
  country: {
    name: string
    slug: string
  }
  participants: {
    name: string
    image: string | undefined
  }[]
  childMarkets: Market[]
  type: string
  isOpen: boolean
  isResolved: boolean
  isCancelled: boolean
  isPaused: boolean
  isOneSideMarket: boolean
  isPlayerPropsMarket: boolean
  isOneSidePlayerPropsMarket: boolean
  isYesNoPlayerPropsMarket: boolean
  playerProps: PlayerProps
  combinedPositions: any[][]
  homeScoreByPeriod: number[]
  awayScoreByPeriod: number[]
  live: boolean
  isGameFinished: boolean
  gameStatus: string
  liveScore: string
  homeScore: number
  awayScore: number
  gameClock: string | null
  gamePeriod: string
  originalMarketName: string
  proof: any[]
  maturity: number
  maturityDate: string
}

export interface TransformedLeague {
  id: number
  slug: string
  name: string
  countrySlug: string
  countryName: string
  tournamentName: string
  turnover: string
  games: TransformedGame[]
}

interface TransformedSport extends Omit<Sport, 'leagues'> {
  leagues: TransformedLeague[]
}

// Convert overtime JSON (single game or array) into the Sport[] format that the UI expects
export const transformOvertimeData = (raw: any): TransformedSport[] => {
  if (!raw) {
    return []
  }
  // console.log({ raw })

  // If raw is an array, keep, else wrap
  const gamesArr = Array.isArray(raw) ? raw : [raw]

  // First pass: collect all tournaments for each league
  const tournamentsByLeague = new Map()
  for (const g of gamesArr) {
    if (!g?.sport || !g?.leagueName) {
      continue
    }

    const leagueKey = `${g.sport}-${g.leagueId}`
    if (!tournamentsByLeague.has(leagueKey)) {
      tournamentsByLeague.set(leagueKey, {
        leagueId: g.leagueId,
        leagueName: g.leagueName,
        tournaments: new Set(),
      })
    }

    if (g.tournamentName && g.tournamentName.trim() !== '') {
      tournamentsByLeague.get(leagueKey).tournaments.add(g.tournamentName)
    }
  }

  // Group by sport and then league (subLeagueId)
  const sportsMap: Record<string, TransformedSport> = {}

  for (const g of gamesArr) {
    if (!g?.sport || !g?.leagueName) {
      continue
    }

    const sportSlug = formatTextToSlug(formatCamelCaseToSpaces(g.sport))
    const sportName = formatCamelCaseToSpaces(g.sport)

    // Apply the same sport name transformations as in transformNavigationData
    const finalSportName =
      sportName === 'Soccer'
        ? 'Football'
        : sportName === 'Football'
          ? 'American Football'
          : sportName

    // Apply the same sport slug transformations as in transformNavigationData
    const finalSportSlug =
      sportSlug === 'soccer'
        ? 'football'
        : sportSlug === 'football'
          ? 'american-football'
          : sportSlug

    // Use the same logic as transformNavigationData for league naming
    const leagueKey = `${g.sport}-${g.leagueId}`
    const leagueInfo = tournamentsByLeague.get(leagueKey)
    const hasMultipleTournaments = leagueInfo && leagueInfo.tournaments.size > 1

    let leagueSlug = formatTextToSlug(g.leagueName)
    let leagueName = g.leagueName
    let leagueId = g.leagueId || g.subLeagueId

    if (
      hasMultipleTournaments &&
      g.tournamentName &&
      g.tournamentName.trim() !== ''
    ) {
      // If there are multiple tournaments, create separate entries for each
      leagueSlug = formatTextToSlug(`${g.leagueName}-${g.tournamentName}`)
      leagueName = `${g.leagueName}-${g.tournamentName}`
      leagueId = `${g.leagueId}-${g.tournamentName}`
    } else if (g.tournamentName && g.tournamentName !== g.leagueName) {
      // Single tournament case - use existing logic
      leagueName = `${g.leagueName} - ${g.tournamentName}`
      leagueSlug = formatTextToSlug(leagueName)
    }

    if (!sportsMap[finalSportName]) {
      sportsMap[finalSportName] = {
        id: g.sport,
        sportId: g.sport,
        slug: finalSportSlug,
        name: finalSportName,
        turnover: '0',
        tournamentsCount: 1,
        countriesCount: 1,
        leagues: [],
      } as TransformedSport
    }

    const sportObj = sportsMap[finalSportName]

    // find or create league inside sport using the new slug format
    let leagueObj = sportObj.leagues.find(l => l.slug === leagueSlug)
    if (!leagueObj) {
      leagueObj = {
        id: leagueId,
        slug: leagueSlug,
        name: leagueName,
        countrySlug: 'international-tournaments',
        countryName: 'International Tournaments',
        tournamentName: g.tournamentName,
        turnover: '0',
        games: [],
      }
      sportObj.leagues.push(leagueObj)
    }

    const participants = [
      { name: g.homeTeam, image: undefined },
      { name: g.awayTeam, image: undefined },
    ]

    const gameObj: TransformedGame = {
      homeTeam: g.homeTeam,
      awayTeam: g.awayTeam,
      leagueId: g.leagueId,
      leagueName: g.leagueName,
      id: g.gameId,
      gameId: g.gameId,
      slug: formatTextToSlug(`${g.homeTeam}-${g.awayTeam}`),
      title: `${g.homeTeam} - ${g.awayTeam}`,
      participants,
      startsAt: g.maturity,
      status: g.status,
      statusCode: g.statusCode,
      odds: g.odds,
      positionNames: g.positionNames,
      line: g.line,
      turnover: '0',
      sport: {
        sportId: g.sport,
        name: finalSportName,
        slug: finalSportSlug,
      },
      league: {
        id: g.leagueId,
        name: formatCamelCaseToSpaces(g.leagueName),
        slug: leagueSlug,
        tournamentName: g.tournamentName,
      },
      country: {
        name: 'International Tournaments',
        slug: 'international-tournaments',
      },
      childMarkets: g.childMarkets.map(market => ({
        ...market,
        odds: market.odds,
        typeId: market.typeId,
        status: market.status,
        type: market.type,
        positionNames: market.positionNames || undefined,
        homeScore: market.homeScore,
        awayScore: market.awayScore,
        gameClock: market.gameClock,
        gamePeriod: market.gamePeriod,
        homeScoreByPeriod: market.homeScoreByPeriod,
        awayScoreByPeriod: market.awayScoreByPeriod,
        live: market.live,
        proof: market.proof || [],
        originalMarketName: market.originalMarketName,
        maturity: market.maturity,
        maturityDate: market.maturityDate,
      })),
      type: g.type,
      typeId: g.typeId,
      isOpen: g.isOpen,
      isResolved: g.isResolved,
      isCancelled: g.isCancelled,
      isPaused: g.isPaused,
      isOneSideMarket: g.isOneSideMarket,
      isPlayerPropsMarket: g.isPlayerPropsMarket,
      isOneSidePlayerPropsMarket: g.isOneSidePlayerPropsMarket,
      isYesNoPlayerPropsMarket: g.isYesNoPlayerPropsMarket,
      playerProps: g.playerProps,
      combinedPositions: g.combinedPositions,
      homeScoreByPeriod: g.homeScoreByPeriod || [],
      awayScoreByPeriod: g.awayScoreByPeriod || [],
      live: g.live,
      isGameFinished: g.isGameFinished || false,
      gameStatus: g.gameStatus || 'unplayed',
      liveScore: g.liveScore || '',
      homeScore: g.homeScore || 0,
      awayScore: g.awayScore || 0,
      gameClock: g.gameClock,
      gamePeriod: g.gamePeriod || '',
      originalMarketName: g.originalMarketName || '',
      proof: g.proof || [],
      maturity: g.maturity,
      maturityDate: g.maturityDate,
    }

    if (leagueObj) {
      leagueObj.games.push(gameObj)
    }
  }

  return Object.values(sportsMap)
}

export default transformOvertimeData
